import { sortBy } from 'lodash';
import { NielsenEventMetadata, NielsenGateway, NielsenLoadContentMetadataValue } from './types';
import { TimetableEntry, toTimetable } from './utils';
import { AdBeginEventArgs } from '../../core/typedEvents';
import { UseTrackingMappedMedia } from '../../plugins/Tracking';
import {
  UnifiedTrackingMachine,
  type UnifiedTrackingMachineAdBeginEvent,
  type UnifiedTrackingMachineAdBreakBeginEvent,
  type UnifiedTrackingMachineEvent,
  UnifiedTrackingMachineEventTypes,
  type UnifiedTrackingMachineNewTrackingEntriesEvent,
  type UnifiedTrackingMachineTimeUpdateEvent,
} from '../tracking/UnifiedTrackingMachine';
import * as Tracking from '../dataProviders/Tracking';
import { sliceCombineRanges } from './sliceCombineRanges';

function fixEpgEntryOrdering(entries: TimetableEntry[]) {
  // We have experienced that epg ordering is buggy in the API, so we will sort the entries by start time
  return sortBy(entries, e => e.start);
}

/**
 * A simple state machine that tracks the current state of the player and sends the appropriate Nielsen events.
 */
export class NielsenTrackingMachine extends UnifiedTrackingMachine {
  #media: UseTrackingMappedMedia | null = null;
  #gateway;
  #lastPlayhead: number | null = null;
  #currentContentMetadata: NielsenLoadContentMetadataValue | null;
  #currentAdMetadata: NielsenLoadContentMetadataValue | null;
  #epgEntries: TimetableEntry[] = [];
  #isInAdBreak;
  #currentAd: AdBeginEventArgs | null = null;
  #initialContentMetadataSent = false;
  #isBuffering: boolean = false;
  #prevPlayhead: number | null = null;
  #hasSentUpcomingProgram: boolean = false;
  #lastMetadata: NielsenLoadContentMetadataValue | null = null;
  #lastEvent: UnifiedTrackingMachineEvent;
  #scheduledAdBreakEnd: any;
  #isStopped: boolean = false;
  private currentBlock: null | NielsenLoadContentMetadataValue | undefined = null;
  private lastBlock: null | NielsenLoadContentMetadataValue | undefined = null;
  #activeEpgEntry:
    | {
        __typename: 'DetailedNielsen';
        trackingObject: string | null;
        ad: boolean | null;
        start: number | null;
        stop: number | null;
      }
    | null
    | undefined;

  private epgUpdateType: string | null;
  private readonly useActive: boolean;

  constructor(gateway: NielsenGateway, useActive = false) {
    super();
    this.#gateway = gateway;
    this.useActive = useActive;
    this.#isInAdBreak = false;
  }

  send(event: UnifiedTrackingMachineEvent) {
    this.#lastEvent = event;
    super.send(event);
  }

  protected onDestroy() {
    if (this.currentBlock) {
      this.#sendEnd();
    }
  }

  protected onBuffering() {
    if (this.currentBlock || this.#currentContentMetadata || this.#currentAdMetadata) {
      this.#sendBuffering();
    }
  }

  protected onPause() {
    if (this.currentBlock || this.#currentContentMetadata || this.#currentAdMetadata) {
      this.#sendStop();
    }
  }

  protected onStop() {
    if (this.#currentContentMetadata || this.#currentAdMetadata) {
      if (this.#currentContentMetadata?.islivestn !== 'y') {
        this.#sendEnd();
      }
    }
  }

  protected onAdEnd() {
    this.#currentAd = null;
    this.#currentAdMetadata = null;
  }

  protected onAdBegin(event: UnifiedTrackingMachineAdBeginEvent) {
    this.#lastPlayhead = null;
    this.#currentAd = event.value;
  }

  protected onAdBreakEnd() {
    if (this.#currentAd) {
      return;
    }
    this.#isInAdBreak = false;

    // If we are in VOD we will set the content metadata after the prerolls
    this.#currentContentMetadata = null;
  }

  protected onAdBreakBegin(event: UnifiedTrackingMachineAdBreakBeginEvent) {
    this.#hasSentUpcomingProgram = false;
    this.#isInAdBreak = true;
    this.#scheduledAdBreakEnd = (this.#lastPlayhead ?? 0) + (event.value.podDuration ?? 0) / 1000;
  }

  protected onSeeking() {
    if (this.currentBlock) {
      this.#sendStop();
    }
  }

  protected onNewTrackingEntries(event: UnifiedTrackingMachineNewTrackingEntriesEvent) {
    const newItems = fixEpgEntryOrdering(toTimetable(Tracking.nielsen(event.value)));

    this.#epgEntries = sliceCombineRanges(this.#epgEntries, newItems);

    this.epgUpdateType =
      event.value.entity && 'epgUpdateType' in event.value.entity
        ? event.value.entity.epgUpdateType
        : null;

    this.#activeEpgEntry =
      event.value.tracking?.__typename === 'LiveTracking'
        ? event.value.tracking.nielsen?.active
        : null;
  }

  protected onTimeUpdate(event: UnifiedTrackingMachineTimeUpdateEvent) {
    let value = Math.floor(event.value.currentTime);

    if (
      this.#media &&
      this.#media.metadata.startOverStreamStartTime &&
      this.#media.metadata.isStartOver
    ) {
      value += Math.floor(this.#media.metadata.startOverStreamStartTime);
    }
    if (value === this.#lastPlayhead || value < 0) return;
    this.#lastPlayhead = value;
    this.#isBuffering = false;

    if (this.#media == null) {
      return;
    }
    if (this.#media?.metadata?.isLive) {
      this.timeupdateLive(value);
    } else {
      this.timeupdateVOD(value);
    }
    this.#prevPlayhead = value;
  }

  protected onLoad(event: {
    type: UnifiedTrackingMachineEventTypes.Load;
    value: { media: UseTrackingMappedMedia; currentTime: number };
  }) {
    this.#media = event.value.media;
  }

  private timeupdateVOD(value: number) {
    const resolvedContentMetadata = this.#mediaToTrackingData(this.#media);
    const resolvedPrerollMetadata = this.#resolvedAdMetadata();
    this.currentBlock = this.#isInAdBreak ? resolvedPrerollMetadata : resolvedContentMetadata;
    if (this.currentBlock && this.currentBlock?.assetid !== this.lastBlock?.assetid) {
      if (this.lastBlock) {
        if (this.lastBlock.type === 'preroll') {
          this.#sendStop(this.#prevPlayhead);
        } else {
          // We need to call `end` at the end of the content.
          this.#sendEnd(this.#prevPlayhead);
        }
      }

      if (this.currentBlock.type === 'preroll' && this.lastBlock?.type !== 'preroll') {
        this.#loadMetadata(resolvedContentMetadata);
      }

      this.#loadMetadata(this.currentBlock);
      this.lastBlock = this.currentBlock;
    }
    if (this.currentBlock || this.#isInAdBreak) {
      this.#sendPlayhead(value, { source: this.#lastEvent });
    }
  }

  /**
   * Handles timeupdate events for live streams, by sending playhead events and load events for content and ads.
   * We will send load events for content and ads if our EPG shows that we have entered new content or ads
   */
  private timeupdateLive(value: number) {
    if (value <= 0) return;
    const resolvedContentMetadata = this.#resolvedContentMetadata(value);
    const resolvedFlowAdMetadata = this.#resolvedFlowAdMetadata(value);
    const resolvedLarMetadata = this.#resolvedAdMetadata();
    this.currentBlock = this.#isInAdBreak
      ? resolvedLarMetadata
      : resolvedContentMetadata ?? resolvedFlowAdMetadata;

    // If in a new block.
    if (this.currentBlock && this.currentBlock?.assetid !== this.lastBlock?.assetid) {
      // Not in the first block.
      if (this.lastBlock) {
        // We need to call "stop" at the end of ads.
        if (this.lastBlock.type === 'ad' || this.lastBlock.type === 'preroll') {
          this.#sendStop(this.#prevPlayhead);
        } else {
          // We need to call `end` at the end of the content.
          this.#sendEnd(this.#prevPlayhead);
        }
      }

      // If we are entering an ad break, we need to send the upcoming program.
      const currentBlockIsAd =
        this.currentBlock.type === 'ad' || this.currentBlock.type === 'preroll';
      const lastBlockWasAd = this.lastBlock?.type === 'ad' || this.lastBlock?.type === 'preroll';
      const isFirstFlowOrLarAdInAdBreak = currentBlockIsAd && !lastBlockWasAd;
      if (isFirstFlowOrLarAdInAdBreak) {
        this.#sendUpcomingProgram();
      }

      this.#loadMetadata(this.currentBlock);
      this.lastBlock = this.currentBlock;
    }
    if (this.currentBlock || this.#isInAdBreak) {
      this.#sendPlayhead(value, { source: this.#lastEvent });
    }
  }

  #sendPlayhead(number: number, metadata?: NielsenEventMetadata) {
    this.#isStopped = false;
    this.#gateway.send('Playhead', number, metadata);
  }

  #sendEnd(playhead: number | null = null) {
    this.#currentContentMetadata = null;
    this.#currentAdMetadata = null;
    this.#gateway.send('End', playhead ?? this.#lastPlayhead! ?? 0, { source: this.#lastEvent });
  }

  #sendStop(playhead: number | null = null) {
    if (this.#isStopped) return;

    this.#isStopped = true;
    this.#gateway.send('Stop', playhead ?? this.#lastPlayhead! ?? 0, { source: this.#lastEvent });
  }

  #sendBuffering(playhead: number | null = null) {
    if (this.#isBuffering) return;

    this.#isBuffering = true;
    this.#gateway.send('Buffering', playhead ?? this.#lastPlayhead! ?? 0, {
      source: this.#lastEvent,
    });
  }

  #findTrackingEntry = (playhead: number) => {
    // If we are in start-over, there's only 1 entry in trackingEntries (epgEntries), which is the one we need
    if (this.#media?.metadata.isStartOver) {
      return this.#epgEntries[0];
    }

    return this.#epgEntries.find(entry => {
      return entry.start <= playhead && entry.stop >= playhead;
    });
  };

  #resolvedContentMetadata = (playhead: number) => {
    if (this.#activeEpgEntry != null && this.epgUpdateType === 'ACTIVE' && this.useActive) {
      const activeEpgEntryMetadata = this.#activeEpgEntry;
      if (activeEpgEntryMetadata.trackingObject) {
        return JSON.parse(activeEpgEntryMetadata.trackingObject);
      }
    }

    const foundEpgEntry = this.#findTrackingEntry(playhead);

    // We are in an ad break, don't track that
    if (foundEpgEntry?.data.metadata.type === 'preroll') {
      return null;
    }

    // If we have found an EPG entry, we will use that.
    // If not, then we will just use the last one we have.
    // If we don't have any, we will use the media metadata.
    return (
      foundEpgEntry?.data?.metadata ??
      this.#currentContentMetadata ??
      this.#mediaToTrackingData(this.#media)
    );
  };

  #resolvedFlowAdMetadata = (playhead: number) => {
    const foundEpgEntry = this.#epgEntries.find(entry => {
      return entry.start <= playhead && entry.stop >= playhead;
    });
    if (foundEpgEntry?.data.metadata.type !== 'preroll') {
      return null;
    }

    return foundEpgEntry?.data?.metadata;
  };

  #resolvedAdMetadata = () => {
    return this.#isInAdBreak && this.#currentAd ? this.#adToTrackingData(this.#currentAd) : null;
  };

  #sendUpcomingProgram() {
    // The upcoming program is just the first program happening after the current playhead that is not an ad or autopromotion
    const foundEpgEntry = this.#epgEntries
      .filter(x => x.data.metadata.type !== 'ad' && x.data.metadata.type !== 'preroll')
      .find(entry => {
        return entry.stop > (this.#scheduledAdBreakEnd ?? this.#lastPlayhead ?? 0);
      });

    if (
      foundEpgEntry &&
      (!this.#currentContentMetadata ||
        !isSameMetadata(foundEpgEntry.data.metadata, this.#currentContentMetadata!))
    ) {
      if (!this.#currentContentMetadata) {
        this.#currentContentMetadata = foundEpgEntry.data.metadata;
      }
    }
    if (foundEpgEntry) {
      this.#loadMetadata(foundEpgEntry?.data?.metadata);
      this.#hasSentUpcomingProgram = true;
    }
  }

  #adToTrackingData(ad: AdBeginEventArgs) {
    const metadata = this.#mediaToTrackingData(this.#media);
    return {
      type: ad.isLar ? 'ad' : 'preroll',
      assetid: ad.isFiller ? 'slate' : ad.creativeId ?? '',
      stationId: metadata.stationId,
      islivestn: metadata.islivestn,
      pbstarttm: this.#lastPlayhead?.toString(),
      subbrand: metadata.subbrand,
    } as NielsenLoadContentMetadataValue;
  }

  #mediaToTrackingData(media: UseTrackingMappedMedia | null): NielsenLoadContentMetadataValue {
    const trackingObject = media?.playback.tracking.nielsen?.trackingObject;
    const obj = trackingObject ? JSON.parse(trackingObject) : null;

    // The API has a bug where length is a number – but it should be a string, according to the Nielsen docs
    if (obj?.length !== undefined) {
      obj.length = obj.length.toString();
    }

    return obj;
  }

  #loadMetadata(currentMetadata: NielsenLoadContentMetadataValue) {
    const toSend = { ...currentMetadata };

    if (currentMetadata.type === 'preroll' && !this.#media?.metadata?.isLive) {
      // Nielsen wants us to set pbstarttm to empty-string for prerolls on VOD
      toSend.pbstarttm = '';
    } else if (
      this.#media?.metadata?.isLive &&
      toSend?.pbstarttm !== undefined &&
      this.#lastPlayhead
    ) {
      // We get a pbstarttm from the API, but it's not correct, so we will change it to our playhead as Nielsen expects
      toSend.pbstarttm = this.#lastPlayhead.toString();
    }

    this.#gateway.send('Load', toSend, { source: this.#lastEvent });
    this.#lastMetadata = toSend;
  }
}

type Metadata = {
  subbrand: string;
  program?: string;
  assetid: string;
  length?: string;
};

function isSameMetadata(a: Metadata, b: Metadata) {
  return (
    a.subbrand === b.subbrand &&
    a.program === b.program &&
    a.assetid === b.assetid &&
    a.length === b.length
  );
}
